import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  ShieldWarning,
  ArrowClockwise,
  WhatsappLogo,
  X,
  DeviceMobile,
  ChatCircleText,
  MonitorPlay,
  Sun,
  Phone,
} from "@phosphor-icons/react";

/**
 * Branded permission-failure modal for Camera / Geolocation.
 *
 * On Android Chrome, when another app draws an overlay (chat bubbles,
 * screen recorders, blue-light filters, Truecaller, floating widgets),
 * the browser silently rejects permission requests with
 * "This site can't ask for your permission". Users are left confused.
 *
 * This modal explains the cause in reassuring language, gives a
 * checklist of the usual culprits, and offers Try Again / Need Help.
 * Rendering it keeps the user on the same onboarding step — nothing
 * is unmounted.
 *
 * Props:
 *   open      : boolean — controls visibility
 *   kind      : "camera" | "location"
 *   onRetry   : () => void  — invoked when the user taps "Try again"
 *   onClose   : () => void  — invoked when the user dismisses the modal
 */
export default function PermissionErrorModal({
  open,
  kind = "camera",
  onRetry,
  onClose,
}) {
  const { t } = useTranslation();

  // Prevent background scroll while the sheet is up.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const isAndroid =
    typeof navigator !== "undefined" && /android/i.test(navigator.userAgent);

  const title = t(`permErr.${kind}.title`);
  const lead = t(`permErr.${kind}.lead`);

  const culprits = [
    { icon: <ChatCircleText size={16} weight="duotone" />, label: t("permErr.culprits.chat") },
    { icon: <MonitorPlay size={16} weight="duotone" />, label: t("permErr.culprits.recorder") },
    { icon: <Phone size={16} weight="duotone" />, label: t("permErr.culprits.truecaller") },
    { icon: <Sun size={16} weight="duotone" />, label: t("permErr.culprits.filter") },
    { icon: <DeviceMobile size={16} weight="duotone" />, label: t("permErr.culprits.floating") },
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="perm-modal"
          data-testid="perm-error-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-3 md:p-6"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-md"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md lele-card p-6 md:p-7 overflow-hidden"
            role="dialog"
            aria-modal="true"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/[0.06] via-transparent to-violet-500/[0.06] pointer-events-none" />
            <div className="lele-grain absolute inset-0 opacity-25 pointer-events-none" />

            <div className="relative">
              <button
                type="button"
                onClick={onClose}
                data-testid="perm-error-close"
                className="absolute -top-1 -right-1 h-8 w-8 rounded-full grid place-items-center text-neutral-500 hover:text-neutral-100 hover:bg-white/[0.05] transition-colors"
                aria-label={t("common.close")}
              >
                <X size={16} weight="bold" />
              </button>

              <div className="flex items-start gap-3 mb-4">
                <div className="h-11 w-11 rounded-xl bg-amber-500/[0.12] border border-amber-400/30 grid place-items-center shrink-0">
                  <ShieldWarning size={22} weight="duotone" className="text-amber-300" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-1">
                    {t("permErr.eyebrow")}
                  </div>
                  <h3 className="font-heading text-xl leading-tight text-neutral-100">
                    {title}
                  </h3>
                </div>
              </div>

              <p className="text-[14px] leading-relaxed text-neutral-300 mb-4">
                {lead}
              </p>

              {isAndroid && (
                <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 mb-4">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-neutral-500 mb-3">
                    {t("permErr.checklistTitle")}
                  </div>
                  <ul className="space-y-2">
                    {culprits.map((c, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2.5 text-[13.5px] text-neutral-200"
                      >
                        <span className="text-fuchsia-300">{c.icon}</span>
                        {c.label}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-3 text-[12px] text-neutral-500 leading-relaxed">
                    {t("permErr.checklistHint")}
                  </p>
                </div>
              )}

              {!isAndroid && (
                <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 mb-4 text-[13px] text-neutral-300 leading-relaxed">
                  {t("permErr.desktopHint")}
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row gap-2 mt-2">
                <a
                  href="https://wa.me/917075521016"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="perm-error-help-btn"
                  className="lele-btn-ghost flex-1 flex items-center justify-center gap-2 text-emerald-200 border-emerald-400/25 hover:border-emerald-400/50 hover:bg-emerald-500/[0.08]"
                >
                  <WhatsappLogo size={15} weight="fill" />
                  {t("permErr.needHelp")}
                </a>
                <button
                  type="button"
                  onClick={onRetry}
                  data-testid="perm-error-retry-btn"
                  className="lele-btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  <ArrowClockwise size={15} weight="bold" />
                  {t("permErr.tryAgain")}
                </button>
              </div>

              <div className="mt-4 text-[11px] uppercase tracking-[0.28em] text-neutral-600 text-center">
                {t("permErr.footer")}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
